/**
 * PATH: packages/ui/src/kanban/components/KanbanCardEntryRollupChip.tsx
 * FILE_ID: UI-KANBAN-CARD-ENTRYCHIP-001
 * PURPOSE: The card-face "Sum" rollup chip over the card's Entries-table
 *          sub-cards — "N entries · M done", plus the summed amount when
 *          any entry carries one.
 * WHY: ARC-3 (U17) page-10 sketch: "Sum" = rollup total on the card
 *      face. Fed EXCLUSIVELY from `card.entry_rollup`, which the BE
 *      computes inside the board list payload from the child_cards
 *      prefetch (snapshot aggregate) — this chip must NEVER fetch.
 * RELATED:
 *   - KanbanCardFooter.tsx: renders this in the triage icon strip
 *   - types/card.ts: EntryRollup shape docs
 */

import { Sigma } from 'lucide-react'

import type { Card } from '../types'

/** 150050 → "$1,500.50"; whole dollars drop the cents. */
export function formatRollupSum(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`
}

export function KanbanCardEntryRollupChip({ card }: { card: Card }) {
  const rollup = card.entry_rollup
  if (!rollup || rollup.count <= 0) return null

  const parts = [
    `${rollup.count} ${rollup.count === 1 ? 'entry' : 'entries'}`,
    `${rollup.done} done`,
  ]
  if (rollup.sum_cents != null) parts.push(formatRollupSum(rollup.sum_cents))

  return (
    <span
      className={[
        'inline-flex items-center gap-0.5',
        rollup.done === rollup.count
          ? 'text-emerald-600 dark:text-emerald-400 font-medium'
          : 'text-sky-600 dark:text-sky-400',
      ].join(' ')}
      title={`Entries rollup: ${parts.join(' · ')} — open the card's Entries table for the rows`}
      aria-label={`Entries: ${parts.join(', ')}`}
      data-testid="entry-rollup-chip"
    >
      <Sigma className="w-3 h-3 shrink-0" />
      {rollup.done}/{rollup.count}
      {rollup.sum_cents != null && (
        <span className="tabular-nums">· {formatRollupSum(rollup.sum_cents)}</span>
      )}
    </span>
  )
}
